import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Stack, TextField, Button, Paper, LinearProgress, Alert, Typography, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useReunionesMetricasReport } from '../../../hooks/useReportJob';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function ReunionesMetricasPage() {
  const { t, i18n } = useTranslation<'translation'>();
  useEffect(() => { dayjs.locale(i18n.language || 'es'); }, [i18n.language]);

  const hoy = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(hoy);
  const invalidRange = useMemo(() => dayjs(desde).isAfter(dayjs(hasta)), [desde, hasta]);

  const { run: runMetricas, useStatus: useStatusMet } = useReunionesMetricasReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatusMet(jobId);

  const generar = async () => {
    if (invalidRange) return;
    const { jobId: j } = await runMetricas({ desde, hasta });
    setJobId(j);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      await downloadReportFile(status.result.url);
    }
  };

  const asistencia = status?.result?.asistencia ?? [];
  const durStats = status?.result?.durStats ?? null;
  const utilizacion = status?.result?.utilizacion ?? [];

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('reunionesMetricas.titulo', 'Métricas de reuniones')}</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField type="date" label={t('common.desde','Desde')} value={desde} onChange={(e)=>setDesde(e.target.value)} size="small" />
        <TextField type="date" label={t('common.hasta','Hasta')} value={hasta} onChange={(e)=>setHasta(e.target.value)} size="small" />
        <Button variant="contained" onClick={generar} disabled={invalidRange || (!!status && !status.done && !status.failed)}>{t('common.generar','Generar')}</Button>
        {invalidRange && <Chip color="warning" label={t('common.rangoInvalido','Rango inválido')} size="small" />}
      </Stack>

      {!!status && !status.done && !status.failed && (
        <Stack spacing={1}>
          <LinearProgress variant="indeterminate" />
          <Typography variant="body2">{t('reunionesMetricas.estado.generando','Generando informe…')}</Typography>
        </Stack>
      )}

      {status?.failed && <Alert severity="error">{t('reunionesMetricas.estado.error','Error generando informe')}</Alert>}

      {status?.done && status.result && (
        <>
          <Alert severity="success">
            {t('reunionesMetricas.estado.exito','Informe generado.')}
            <Button variant="text" size="small" onClick={handleDownload} sx={{ ml: 2 }}>{t('reunionesMetricas.descargar','Descargar métricas')}</Button>
          </Alert>

          <Paper sx={{ p:2 }}>
            <Typography variant="subtitle2">Asistencia por canal</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Canal</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Realizadas</TableCell>
                  <TableCell align="right">No-shows</TableCell>
                  <TableCell align="right">Tasa asistencia (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asistencia.map((r:any)=>(
                  <TableRow key={r.canal}>
                    <TableCell>{r.canal}</TableCell>
                    <TableCell align="right">{r.total}</TableCell>
                    <TableCell align="right">{r.realizadas}</TableCell>
                    <TableCell align="right">{r.noShows}</TableCell>
                    <TableCell align="right">{r.tasaAsistencia}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p:2 }}>
            <Typography variant="subtitle2">Duración reuniones (min)</Typography>
            {durStats ? (
              <Box>
                <Typography>Min: {durStats.min} · Avg: {durStats.avg} · Max: {durStats.max} · Count: {durStats.count}</Typography>
              </Box>
            ) : <Typography>—</Typography>}
          </Paper>

          <Paper sx={{ p:2 }}>
            <Typography variant="subtitle2">Utilización staff (horas)</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Staff</TableCell>
                  <TableCell align="right">Horas reunión</TableCell>
                  <TableCell align="right">Horas disponibles</TableCell>
                  <TableCell align="right">Ocupación %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {utilizacion.map((r:any)=>(
                  <TableRow key={r.idStaff}>
                    <TableCell>{r.staffNombre ?? `#${r.idStaff}`}</TableCell>
                    <TableCell align="right">{r.horasReunion}</TableCell>
                    <TableCell align="right">{r.horasDisponibles}</TableCell>
                    <TableCell align="right">{r.ocupacionPct}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}

